/**
 * Research:
 * https://ben.akrin.com/?p=7888 - An HTML5 canvas Flood Fill that doesn’t kill the browser
 * https://stackoverflow.com/questions/828905/polygon-enclosing-a-set-of-points
 * https://en.wikipedia.org/wiki/Convex_hull
 * http://ubicomp.algoritmi.uminho.pt/local/concavehull.html
 * https://gis.stackexchange.com/questions/1200/what-are-definition-algorithms-and-practical-solutions-for-concave-hull
 * https://stackoverflow.com/questions/83593/is-there-an-efficient-algorithm-to-generate-a-2d-concave-hull
 * https://github.com/mapbox/concaveman
 * https://github.com/AndriiHeonia/hull
 */

import concaveman from "concaveman"
import * as StackBlur from "stackblur-canvas"
import { fromImage } from "./potrace"

interface Color {
  r: number
  g: number
  b: number
  a: number
}

const floodFill = (x: number, y: number, pixels: ImageData, color: Color) => {
  const pixelStack = [{ x, y }]

  let linearCoords = (y * pixels.width + x) * 4

  const originalColor = {
    r: pixels.data[linearCoords],
    g: pixels.data[linearCoords + 1],
    b: pixels.data[linearCoords + 2],
    a: pixels.data[linearCoords + 3],
  }

  while (pixelStack.length > 0) {
    const newPixel = pixelStack.shift()

    if (!newPixel) {
      console.error("newPixel is undefined")
      return pixels
    }

    x = newPixel.x
    y = newPixel.y

    linearCoords = (y * pixels.width + x) * 4

    while (
      y-- >= 0 &&
      pixels.data[linearCoords] === originalColor.r &&
      pixels.data[linearCoords + 1] === originalColor.g &&
      pixels.data[linearCoords + 2] === originalColor.b &&
      pixels.data[linearCoords + 3] === originalColor.a
    ) {
      linearCoords -= pixels.width * 4
    }

    linearCoords += pixels.width * 4
    y++

    let reachedLeft = false
    let reachedRight = false

    while (
      y++ < pixels.height &&
      pixels.data[linearCoords] === originalColor.r &&
      pixels.data[linearCoords + 1] === originalColor.g &&
      pixels.data[linearCoords + 2] === originalColor.b &&
      pixels.data[linearCoords + 3] === originalColor.a
    ) {
      pixels.data[linearCoords] = color.r
      pixels.data[linearCoords + 1] = color.g
      pixels.data[linearCoords + 2] = color.b
      pixels.data[linearCoords + 3] = color.a

      if (x > 0) {
        if (
          pixels.data[linearCoords - 4] === originalColor.r &&
          pixels.data[linearCoords - 4 + 1] === originalColor.g &&
          pixels.data[linearCoords - 4 + 2] === originalColor.b &&
          pixels.data[linearCoords - 4 + 3] === originalColor.a
        ) {
          if (!reachedLeft) {
            pixelStack.push({ x: x - 1, y })
            reachedLeft = true
          }
        } else if (reachedLeft) {
          reachedLeft = false
        }
      }

      if (x < pixels.width - 1) {
        if (
          pixels.data[linearCoords + 4] === originalColor.r &&
          pixels.data[linearCoords + 4 + 1] === originalColor.g &&
          pixels.data[linearCoords + 4 + 2] === originalColor.b &&
          pixels.data[linearCoords + 4 + 3] === originalColor.a
        ) {
          if (!reachedRight) {
            pixelStack.push({ x: x + 1, y })
            reachedRight = true
          }
        } else if (reachedRight) {
          reachedRight = false
        }
      }

      linearCoords += pixels.width * 4
    }
  }

  return pixels
}

const threshold = (pixels: ImageData) => {
  const pxData = pixels.data
  const max = pxData.length
  let i = 0

  for (i; i < max; i += 4) {
    pxData[i] = pxData[i + 1] = pxData[i + 2] = pxData[i + 3] === 0 ? 255 : 0
    pxData[i + 3] = 255
  }

  return pixels
}

const opaquePoints = (pixels: ImageData) => {
  const pxData = pixels.data
  const width = pixels.width
  const max = pxData.length
  const points: number[][] = []
  let i = 0

  for (i; i < max; i += 4) {
    if (pxData[i + 3] !== 0) {
      const x = (i / 4) % width
      const y = Math.floor(i / 4 / width)
      points.push([x, y])
    }
  }

  return points
}

const contourCanvas = (imageData: ImageData, concavity: number) => {
  const c = document.createElement("canvas")
  const ctx = c.getContext("2d")

  if (!ctx) {
    throw new Error("Unable to construct canvas")
  }

  const outlinePoints = opaquePoints(imageData)

  c.width = imageData.width
  c.height = imageData.height

  if (outlinePoints.length === 0) {
    outlinePoints.push([0, 0])
  }

  const concavePolygon = concaveman(outlinePoints, concavity, 0)
  const start = concavePolygon.shift()

  if (!start || !Array.isArray(start) || start.length < 2 || start[0] === null || start[1] === null) {
    throw new Error("Canvas contour points are empty")
  }

  ctx.strokeStyle = "black"
  ctx.fillStyle = "black"

  ctx.beginPath()
  ctx.moveTo(start[0], start[1])

  for (const point of concavePolygon) {
    ctx.lineTo(point[0], point[1])
  }

  ctx.closePath()
  ctx.stroke()
  ctx.fill()

  return ctx.getImageData(0, 0, c.width, c.height)
}

const contourPath = (
  source: ImageData,
  blur: number,
  scaleDiff: number,
  scaleX?: number,
  scaleY?: number,
  forcedWidth?: number,
  forcedHeight?: number
): ContourData => {
  const sourceWidth = source.width
  const sourceHeight = source.height

  let imageData = source

  // threshold image
  imageData = threshold(imageData)

  // flood fill all holes with black
  imageData = floodFill(0, 0, imageData, { r: 0, g: 0, b: 0, a: 0 })

  // contour initial image
  imageData = contourCanvas(imageData, 0.5)

  // apply blur to contour for padding
  StackBlur.imageDataRGBA(imageData, 0, 0, imageData.width, imageData.height, Math.round(blur))

  // contour blurred image
  imageData = contourCanvas(imageData, 1)

  // threshold again
  imageData = threshold(imageData)

  // create virtual canvas
  const c = document.createElement("canvas")
  const ctx = c.getContext("2d")

  if (!ctx) {
    throw new Error("Unable to construct canvas")
  }

  // draw image to virtual canvas
  c.width = sourceWidth
  c.height = sourceHeight
  ctx.putImageData(imageData, 0, 0)

  // convert to path with potrace
  const pathList = fromImage(c)

  return {
    path: pathList.toSVGpathString(1),
    width: sourceWidth,
    height: sourceHeight,
    scaleDiff,
    scaleX,
    scaleY,
    forcedWidth,
    forcedHeight,
  }
}

export default contourPath
